import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Container, TextField, Typography, CircularProgress } from "@mui/material";
import {
  fetchSearchDeeplinksInfo,
  saveSearchDeeplinksInfo,
} from "../../actions/searchDeeplinksActions";
import {
  uploadImage,
  clearMedia
} from "../../actions/mediaActions";
import ImageContainerPreview from "../Common/ImageContainerPreview";
import Swal from "sweetalert2";
import { preventEnterFormSubmission } from '../../utils/formUtils';

const SearchDeeplinks = () => {
  const dispatch = useDispatch();
  const deeplinks = useSelector(state => state.search.deeplinks);
  const uploadedImage = useSelector(state => state.media?.image);

  const [searchDeeplinkImageURL, setSearchDeeplinkImageURL] = useState("");
  const [searchDeeplinkIOSURL, setSearchDeeplinkIOSURL] = useState("");
  const [searchDeeplinkAndroidURL, setSearchDeeplinkAndroidURL] = useState("");
  const [deeplinkImageUploading, setDeeplinkImageUploading] = useState(false);
  const [edited, setEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchSearchDeeplinksInfo());
        await dispatch(clearMedia());
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: "Failed to fetch deeplinks information. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // Update state when deeplinks data changes
  useEffect(() => {
    if (deeplinks) {
      setSearchDeeplinkImageURL(deeplinks.deeplink_image_url || "");
      setSearchDeeplinkIOSURL(deeplinks.deeplink_ios || "");
      setSearchDeeplinkAndroidURL(deeplinks.deeplink_android || "");
    }
  }, [deeplinks]);

  // Handle image upload completion
  useEffect(() => {
    if (uploadedImage && deeplinkImageUploading) {
      setSearchDeeplinkImageURL(uploadedImage);
      setDeeplinkImageUploading(false);
      setEdited(true);
      dispatch(clearMedia());
    }
  }, [uploadedImage, deeplinkImageUploading, dispatch]);

  const handleDeepLinkImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setDeeplinkImageUploading(true);
      dispatch(uploadImage(file, e.target.name));
    }
  }, [dispatch]);

  const removeImage = useCallback(() => {
    setSearchDeeplinkImageURL("");
    setEdited(true);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      await dispatch(saveSearchDeeplinksInfo(
        searchDeeplinkImageURL,
        searchDeeplinkIOSURL,
        searchDeeplinkAndroidURL
      ));
      setEdited(false);
      Swal.fire({
        icon: "success",
        text: "Deeplinks information saved successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: "Failed to save deeplinks information. Please try again.",
      });
    }
  }, [dispatch, searchDeeplinkImageURL, searchDeeplinkIOSURL, searchDeeplinkAndroidURL]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Box my={4}>
        <form onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '2fr 3fr'
              },
              gap: 3,
              alignItems: 'flex-start'
            }}
          >
            {/* Image Upload Section */}
            <Box>
              <ImageContainerPreview
                title="Deeplink Image"
                image={searchDeeplinkImageURL}
                name="share_image_url"
                handleRemove={removeImage}
                handleImageUpload={handleDeepLinkImageUpload}
                showCopyUrlButton={true}
                sx={{
                  padding: 5,
                  background: '#FFFCF5',
                  height: 250,
                  objectFit: 'contain',
                  width: '100%'
                }}
              />
            </Box>

            {/* Fields Section */}
            <Box>
              {/* iOS Deeplink Input */}
              <Box mb={3}>
                <TextField
                  fullWidth
                  label="Deeplink iOS"
                  variant="outlined"
                  value={searchDeeplinkIOSURL}
                  onChange={(e) => {
                    setSearchDeeplinkIOSURL(e.target.value);
                    setEdited(true);
                  }}
                  error={!searchDeeplinkIOSURL && edited}
                  helperText={!searchDeeplinkIOSURL && edited ? "iOS deeplink is required" : ""}
                />
              </Box>

              {/* Android Deeplink Input */}
              <Box mb={3}>
                <TextField
                  fullWidth
                  label="Deeplink Android"
                  variant="outlined"
                  value={searchDeeplinkAndroidURL}
                  onChange={(e) => {
                    setSearchDeeplinkAndroidURL(e.target.value);
                    setEdited(true);
                  }}
                  error={!searchDeeplinkAndroidURL && edited}
                  helperText={!searchDeeplinkAndroidURL && edited ? "Android deeplink is required" : ""}
                />
              </Box>
            </Box>
          </Box>

          {/* Save Button */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!edited}
              sx={{
                width: {
                  xs: '100%',  // Full width on mobile
                  sm: '50%',   // Half width on tablet
                  md: '35%'    // 35% width on desktop
                }
              }}
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default SearchDeeplinks;
